import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { I18nProvider } from './contexts/I18nContext';
import { ProtectedRoute, PageLoad, useFont } from './components/UI';
import Layout from './components/Layout';
import SupplierAgreementGate from './components/SupplierAgreementGate';
import ActivationGate from './components/ActivationGate';

// Public pages
const Home        = lazy(() => import('./pages/Home'));
const Activate     = lazy(() => import('./pages/Activate'));

// Customer pages
const CustDash      = lazy(() => import('./pages/customer/Dashboard'));
const CustProfile   = lazy(() => import('./pages/customer/Profile'));

// Supplier pages
const SuppDash    = lazy(() => import('./pages/supplier/Dashboard'));
const SuppProfile = lazy(() => import('./pages/supplier/Profile'));

// Shared
const Settings    = lazy(() => import('./pages/Settings'));
const Notifications = lazy(() => import('./pages/Notifications'));
const BlockedUsers = lazy(() => import('./pages/BlockedUsers'));
const SupplierAgreement = lazy(() => import('./pages/supplier/Agreement'));
const Trust       = lazy(() => import('./pages/Trust'));

// Admin
const AdminDash   = lazy(() => import('./pages/admin/Dashboard'));
const TrustModeration = lazy(() => import('./pages/admin/TrustModeration'));
const AdminSupport    = lazy(() => import('./pages/admin/Support'));

function Inner() {
  useFont();
  return (
    <Suspense fallback={<PageLoad />}>
      <Routes>
        {/* ── Public ── */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Home />} />
        <Route path="/register" element={<Home />} />
        <Route path="/forgot-password" element={<Home />} />
        <Route path="/reset-password" element={<Home />} />

        {/* ── Customer activation (standalone, no layout) ── */}
        <Route path="/customer/activate" element={
          <ProtectedRoute roles={['customer']}><Activate /></ProtectedRoute>
        } />

        {/* ── Customer ── */}
        <Route path="/customer" element={
          <ProtectedRoute roles={['customer']}>
            <ActivationGate>
              <Layout role="customer" />
            </ActivationGate>
          </ProtectedRoute>
        }>
          <Route index element={<CustDash />} />
          <Route path="profile" element={<CustProfile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="blocked" element={<BlockedUsers />} />
          <Route path="trust" element={<Trust />} />
        </Route>

        {/* ── Supplier agreement (standalone, no layout) ── */}
        <Route path="/supplier/agreement" element={
          <ProtectedRoute roles={['supplier']}><SupplierAgreement /></ProtectedRoute>
        } />
        <Route path="/supplier/activate" element={
          <ProtectedRoute roles={['supplier']}><Activate /></ProtectedRoute>
        } />

        {/* ── Supplier ── */}
        <Route path="/supplier" element={
          <ProtectedRoute roles={['supplier']}>
            <ActivationGate>
              <SupplierAgreementGate>
                <Layout role="supplier" />
              </SupplierAgreementGate>
            </ActivationGate>
          </ProtectedRoute>
        }>
          <Route index element={<SuppDash />} />
          <Route path="profile" element={<SuppProfile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="blocked" element={<BlockedUsers />} />
          <Route path="trust" element={<Trust />} />
        </Route>

        {/* ── Admin ── */}
        <Route path="/admin" element={
          <ProtectedRoute roles={['admin']}>
            <Layout role="admin" />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDash />} />
          <Route path="suppliers" element={<AdminDash tab="suppliers" />} />
          <Route path="users" element={<AdminDash tab="users" />} />
          <Route path="work-orders" element={<AdminDash tab="workorders" />} />
          <Route path="trust" element={<TrustModeration />} />
          <Route path="support" element={<AdminSupport />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: { fontFamily: "'Inter',sans-serif", fontSize: 13, fontWeight: 600, borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.12)' },
          }}
        />
        <Inner />
      </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}
